import { CandidateApiService } from 'candidate/CandidateApiService';
import { CandidateActiveCv, CandidatesGetActiveCvList } from 'candidate/types';
import { JobApiService } from 'jobs/JobApiService';
import { CandidateCV, JobApplyGetFileList } from 'jobs/types';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { AsyncState } from 'shared/libs/state';

export class ResumeChooserStore {
   readonly loadCvListsState = new AsyncState<
      [PromiseSettledResult<JobApplyGetFileList>, PromiseSettledResult<CandidatesGetActiveCvList>]
   >();

   cvList: CandidateCV[] = [];
   activeCvList: CandidateActiveCv[] = [];

   file: File | null = null;
   selectedResume: CandidateCV | CandidateActiveCv | null = null;
   message = ''

   constructor() {
      makeObservable(this, {
         cvList: observable,
         activeCvList: observable,
         selectedResume: observable,
         file: observable,
         message: observable,
         selectResume: action,
         selectResumeByValue: action,
         setFile: action,
         setMessage: action,
         resumeViewValue: computed,
         selectedResumeValue: computed,
         loading: computed,
      });
   }

   selectResume(cv: CandidateCV | CandidateActiveCv | null): void {
      this.file = null;
      this.selectedResume = cv;
   }

   setMessage(m: string): void {
      this.message = m;
   }

   setFile(file: File) {
      this.selectedResume = null;
      this.file = file;
   }

   selectResumeByValue(value: string): void {
      const [resumeType, resumeId] = value.split(':');
      const parsedId = Number(resumeId);

      if (Number.isNaN(parsedId)) {
         this.selectResume(null);
         return;
      }

      if (resumeType === 'active') {
         this.selectResume(this.activeCvList.find((cv) => cv.id === parsedId) ?? null);
         return;
      }

      if (resumeType === 'uploaded') {
         this.selectResume(this.cvList.find((cv) => cv.id === parsedId) ?? null);
         return;
      }

      this.selectResume(null);
   }

   get resumeViewValue(): string {
      if (this.selectedResume) {
         if ('file' in this.selectedResume) {
            return this.selectedResume.file.name;
         } else {
            return this.selectedResume.name;
         }
      } else {
         return '';
      }
   }

   get selectedResumeValue(): string {
      if (!this.selectedResume) {
         return '';
      }

      if ('file' in this.selectedResume) {
         return `uploaded:${this.selectedResume.id}`;
      }

      return `active:${this.selectedResume.id}`;
   }

   get loading(): boolean {
      return this.loadCvListsState.status === 'pending';
   }

   loadCvLists = async () => {
      await this.loadCvListsState.run(
         () =>
            Promise.allSettled([
               JobApiService.jobApplyGetFileList(),
               CandidateApiService.candidatesGetActiveCvList(),
            ]),
         {
            onSuccess: ([resApplyCv, resCandidateActiveCv]) => {
               runInAction(() => {
                  if (resApplyCv.status === 'fulfilled' && resApplyCv.value.status === 200) {
                     this.cvList = resApplyCv.value.response.filesList;
                  }

                  if (
                     resCandidateActiveCv.status === 'fulfilled' &&
                     resCandidateActiveCv.value.status === 200
                  ) {
                     this.activeCvList = resCandidateActiveCv.value.response;
                  }
               });
            },
         }
      );
   };
}
